import {
  addFavorite,
  removeFavorite,
  setFavorites,
} from "store/favorites/favoritesSlice";
import { createAsyncThunk } from "@reduxjs/toolkit";
import { resetAuthState, setCurrentUser } from "./authSlice";
import {
  getUserType,
  setUserType,
  clearUserType,
} from "helpers/userTypeHelpers";
import { getToken, setToken, clearToken } from "helpers/tokenHelpers";
import axios from "axios";
import { API_BASE_URL } from "config";

const baseUrl = API_BASE_URL;

// ============================================================
// Helper: Clear all booking-related sessionStorage
// ============================================================
const clearBookingSessionData = () => {
  console.log('[THUNKS] 🧹 Clearing booking session data');
  
  const bookingKeys = [
    'pending_booking_data',
    'booking_space_id',
    'booking_form_processing',
    'booking_form_processing_message',
    'booking_flow_start_time',
    'booking_completed',
    'processing_booking_in_progress',
    'processing_started',
    'just_logged_in_for_booking',
    'just_registered_for_booking',
    'has_pending_booking_after_oauth',
    'signup_booking_context',
    'google_registration_data',
    'google_oauth_state',
    'google_oauth_intent',
    'google_signup_type',
    'google_registration_complete',
    'oauth_temp_code',
    'oauth_temp_state',
    'oauth_temp_scope',
    'oauth_return_url',
    'booking_form_context',
    'registration_timestamp',
    'login_timestamp',
    'post_login_return_url',
  'pending_basket_redirect',
  ];
  
  // Remove specific keys
  bookingKeys.forEach(key => {
    if (sessionStorage.getItem(key)) {
      console.log(`[THUNKS] Removing: ${key}`);
      sessionStorage.removeItem(key);
    }
  });
  
  // Remove dynamic keys (with space IDs)
  const keysToRemove = [];
  for (let i = 0; i < sessionStorage.length; i++) {
    const key = sessionStorage.key(i);
    if (key && (
      key.startsWith('booking_completed_success_') ||
      key.startsWith('restoration_done_') ||
      key.startsWith('processed_pending_') ||
      key.startsWith('pending_processed_')
    )) {
      keysToRemove.push(key);
    }
  }
  
  keysToRemove.forEach(key => {
    console.log(`[THUNKS] Removing dynamic key: ${key}`);
    sessionStorage.removeItem(key);
  });
  
  console.log('[THUNKS] ✅ Booking session data cleared');
};

// Base API instance with axios
const api = axios.create({
  baseURL: baseUrl,
});

// Helper function for error handling
const handleError = (error, defaultMessage = "error") => {
  return error.response?.data.errors || defaultMessage;
};

// Enhanced error handler
const handleAuthError = (error, thunkAPI) => {
  if (error.response?.data?.errors?.some(err => err.code === 'token_expired')) {
    // Clear all auth-related data
    clearToken();
    thunkAPI.dispatch(resetAuthState());
    thunkAPI.dispatch(setFavorites([]));
  }
  return error.response?.data?.errors || [{ message: 'An error occurred' }];
};

// Thunks

export const loginWithGoogle = createAsyncThunk(
  "auth/loginWithGoogle",
  async ({ code, clientId, redirectUri, targetUid, forBookingForm = false, spaceId = null }, { rejectWithValue, dispatch }) => {
    try {
      console.log("[GOOGLE LOGIN] Step 1: Exchanging code for user data...");
      
      // Set flag for booking form login - KEEP these until booking is complete
      if (forBookingForm && spaceId) {
        sessionStorage.setItem('just_logged_in_for_booking', 'true');
        sessionStorage.setItem('booking_space_id', spaceId);
        sessionStorage.setItem('login_timestamp', Date.now().toString());
        console.log("[GOOGLE LOGIN] Set booking login flag");
      }
      
      const response = await fetch(`${baseUrl}auth/google`, {
        method: "POST",
        headers: {
          "Content-Type": "application/json",
          "Accept": "application/json"
        },
        body: JSON.stringify({ 
          code, 
          clientId, 
          redirectUri,
          uid: targetUid || null
        }),
      });
      
      const data = await response.json();
      console.log("[GOOGLE LOGIN] User data response:", data);
      
      // Handle "Not registered user" case (new user)
      if (data.message === "Not registered user" && data.data) {
        console.log("[GOOGLE LOGIN] New user - needs registration");
        return rejectWithValue({ 
          message: "Please complete registration",
          userData: data.data,
          needsRegistration: true
        });
      }
      
      // Check if we already have a token in the response
      if (data.data?.token) {
        console.log("[GOOGLE LOGIN] Token found in response");
        const token = data.data.token;
        const user = data.data.user || data.data;
        
        localStorage.setItem('token', token);
        localStorage.setItem('accessToken', token);
        
        if (user?.type) {
          localStorage.setItem('userType', user.type);
        }
        
        // CRITICAL: DO NOT clear booking flags here - let CompactBookingForm handle it
        // The flags will be cleared after the booking is complete
        
        return {
          data: { token, user },
          message: data.message || "Google login successful"
        };
      }
      
      // If no token, we need to get one from the authenticate endpoint
      if (data.data?.email) {
        console.log("[GOOGLE LOGIN] No token in response, getting token from authenticate endpoint...");
        
        // Get a REAL token from the backend
        const loginResponse = await fetch(`${baseUrl}authenticate?lang=en`, {
          method: "POST",
          headers: {
            "Content-Type": "application/json",
            "Accept": "application/json"
          },
          body: JSON.stringify({
            email: data.data.email,
            google: data.data.google // Pass the google ID
          }),
        });
        
        const loginData = await loginResponse.json();
        console.log("[GOOGLE LOGIN] Authentication response:", loginData);
        
        if (loginData.data?.token) {
          const token = loginData.data.token;
          const user = loginData.data.user;
          
          localStorage.setItem('token', token);
          localStorage.setItem('accessToken', token);
          
          if (user?.type) {
            localStorage.setItem('userType', user.type);
          }
          
          // CRITICAL: DO NOT clear booking flags here - let CompactBookingForm handle it
          
          return {
            data: { token, user },
            message: "Google login successful"
          };
        }
        
        throw new Error("Could not authenticate user");
      }
      
      throw new Error("Invalid response from server");
      
    } catch (error) {
      console.error("[GOOGLE LOGIN] Error:", error);
      // Clear flags on error
      sessionStorage.removeItem('just_logged_in_for_booking');
      sessionStorage.removeItem('booking_space_id');
      sessionStorage.removeItem('login_timestamp');
      return rejectWithValue([{ message: error.message || "Google login failed" }]);
    }
  }
);

export const completeSocialRegistration = createAsyncThunk(
  "auth/completeSocialRegistration",
  async ({ provider, userData, userType, status, language, forBookingForm = false, spaceId = null, captcha_token = null }, { rejectWithValue, dispatch }) => {
    try {
      console.log("[SOCIAL REGISTRATION] 🔍 Completing registration for:", userData.email);
      console.log("[SOCIAL REGISTRATION] 📝 Received captcha_token:", captcha_token);
      console.log("[SOCIAL REGISTRATION] 📝 forBookingForm:", forBookingForm);
      console.log("[SOCIAL REGISTRATION] 📝 spaceId:", spaceId);
      
      // Check if using test key
      const isTestKey = process.env.REACT_APP_TURNSTILE_SITE_KEY === '1x00000000000000000000AA';
      
      // Get the token from parameter
      let finalCaptchaToken = captcha_token;
      
      // If using test key and no token, generate one
      if (!finalCaptchaToken && isTestKey) {
        finalCaptchaToken = '1x' + Date.now().toString(36) + '_' + Math.random().toString(36).substr(2, 9);
        console.log("[SOCIAL REGISTRATION] 🔧 Generated test token:", finalCaptchaToken);
      }
      
      console.log("[SOCIAL REGISTRATION] 📝 Final captcha_token:", finalCaptchaToken);
      
      const lang = language || 'en';
      const finalLang = lang === "be" ? "fr" : lang;
      
      // Extract Google ID
      const googleId = userData.google || userData.google_id || userData.sub;
      
      console.log("[SOCIAL REGISTRATION] Extracted Google ID:", googleId);
      
      if (!googleId) {
        console.error("[SOCIAL REGISTRATION] No Google ID found");
        return rejectWithValue([{ message: "Missing Google ID. Please try again." }]);
      }
      
      // Step 1: Register the user
      const registrationData = {
        email: userData.email,
        first_name: userData.first_name,
        last_name: userData.last_name,
        type: userType,
        status: status,
        google: googleId,
        lang: finalLang,
        domain: userData.domain,
        cgu: true,
        newsletter: false,
        captcha_token: finalCaptchaToken
      };
      
      // Add phone if provided
      if (userData.phone) {
        registrationData.phone = userData.phone;
      }
      
      // Add social_reason if provided
      if (userData.social_reason) {
        registrationData.social_reason = userData.social_reason;
      }
      
      console.log("[SOCIAL REGISTRATION] Registration payload:", registrationData);
      
      const registerResponse = await fetch(`${baseUrl}users?lang=${finalLang}`, {
        method: "POST",
        headers: {
          "Content-Type": "application/json",
          "Accept": "application/json"
        },
        body: JSON.stringify(registrationData),
      });
      
      const registerData = await registerResponse.json();
      console.log("[SOCIAL REGISTRATION] Registration response:", registerData);
      
      if (!registerResponse.ok) {
        if (registerData.errors && Array.isArray(registerData.errors)) {
          // Check for CAPTCHA errors
          const hasCaptchaError = registerData.errors.some(err => 
            err.code === 'captcha_token' || 
            err.message?.toLowerCase().includes('captcha') ||
            err.code === 'captcha'
          );
          if (hasCaptchaError) {
            console.log("[SOCIAL REGISTRATION] ❌ CAPTCHA validation failed");
          }
          // Clear flags on error
          sessionStorage.removeItem('just_registered_for_booking');
          sessionStorage.removeItem('just_logged_in_for_booking');
          sessionStorage.removeItem('booking_space_id');
          sessionStorage.removeItem('registration_timestamp');
          sessionStorage.removeItem('has_pending_booking_after_oauth');
          return rejectWithValue(registerData.errors);
        }
        throw new Error(registerData.message || "Registration failed");
      }
      
      console.log("[SOCIAL REGISTRATION] ✅ Registration successful!");
      
      // ============================================================
      // CRITICAL FIX: Login the user after successful registration
      // ============================================================
      try {
        console.log("[SOCIAL REGISTRATION] 🔐 Logging in user...");
        
        // Call the login endpoint to get a token
        const loginResponse = await fetch(`${baseUrl}authenticate?lang=${finalLang}`, {
          method: "POST",
          headers: {
            "Content-Type": "application/json",
            "Accept": "application/json"
          },
          body: JSON.stringify({
            email: userData.email,
            google: googleId
          }),
        });
        
        const loginData = await loginResponse.json();
        console.log("[SOCIAL REGISTRATION] Login response:", loginData);
        
        if (loginData.data?.token) {
          const token = loginData.data.token;
          const user = loginData.data.user;
          
          localStorage.setItem('token', token);
          localStorage.setItem('accessToken', token);
          
          if (user?.type) {
            localStorage.setItem('userType', user.type);
          }
          
          console.log("[SOCIAL REGISTRATION] ✅ User logged in successfully");
          
          // ============================================================
          // CRITICAL FIX: DO NOT clear booking flags on success
          // Let the UI handle clearing them after booking is complete
          // ============================================================
          // Only remove these specific items:
          sessionStorage.removeItem('registration_timestamp');
          sessionStorage.removeItem('google_registration_data');
          
          // ============================================================
          // PRESERVE booking flags for the booking flow
          // ============================================================
          // DO NOT remove:
          // - 'just_registered_for_booking' - needed for booking flow
          // - 'booking_space_id' - needed for redirect
          // - 'has_pending_booking_after_oauth' - needed for booking flow
          // - 'pending_booking_data' - needed for CompactBookingForm
          
          console.log("[SOCIAL REGISTRATION] ✅ Booking flags preserved for continuation");
          console.log("[SOCIAL REGISTRATION] 📝 just_registered_for_booking:", sessionStorage.getItem('just_registered_for_booking'));
          console.log("[SOCIAL REGISTRATION] 📝 booking_space_id:", sessionStorage.getItem('booking_space_id'));
          console.log("[SOCIAL REGISTRATION] 📝 has_pending_booking_after_oauth:", sessionStorage.getItem('has_pending_booking_after_oauth'));
          
          return {
            data: { token, user },
            message: "Registration successful"
          };
        } else {
          console.error("[SOCIAL REGISTRATION] ❌ No token in login response");
          throw new Error("Login failed after registration");
        }
        
      } catch (loginError) {
        console.error("[SOCIAL REGISTRATION] ❌ Login error:", loginError);
        // Clear flags on login error
        sessionStorage.removeItem('just_registered_for_booking');
        sessionStorage.removeItem('just_logged_in_for_booking');
        sessionStorage.removeItem('booking_space_id');
        sessionStorage.removeItem('registration_timestamp');
        sessionStorage.removeItem('has_pending_booking_after_oauth');
        return rejectWithValue([{ message: "Login failed after registration. Please try logging in manually." }]);
      }
      
    } catch (error) {
      console.error("[SOCIAL REGISTRATION] ❌ Error:", error);
      // Clear flags on error
      sessionStorage.removeItem('just_registered_for_booking');
      sessionStorage.removeItem('just_logged_in_for_booking');
      sessionStorage.removeItem('booking_space_id');
      sessionStorage.removeItem('registration_timestamp');
      sessionStorage.removeItem('has_pending_booking_after_oauth');
      return rejectWithValue([{ message: error.message || "Registration failed" }]);
    }
  }
);



// export const completeSocialRegistration = createAsyncThunk(
//   "auth/completeSocialRegistration",
//   async ({ provider, userData, userType, status, language, forBookingForm = false, spaceId = null, captcha_token = null }, { rejectWithValue, dispatch }) => {
//     try {
//       console.log("[SOCIAL REGISTRATION] 🔍 Completing registration for:", userData.email);
//       // console.log("[SOCIAL REGISTRATION] 📝 Received captcha_token:", captcha_token);
//       console.log("[SOCIAL REGISTRATION] 📝 forBookingForm:", forBookingForm);
//       console.log("[SOCIAL REGISTRATION] 📝 spaceId:", spaceId);
      
//       // // Check if using test key
//       // const isTestKey = process.env.REACT_APP_TURNSTILE_SITE_KEY === '1x00000000000000000000AA';
//       // 
//       // // Get the token from parameter
//       // let finalCaptchaToken = captcha_token;
//       // 
//       // // If using test key and no token, generate one
//       // if (!finalCaptchaToken && isTestKey) {
//       //   finalCaptchaToken = '1x' + Date.now().toString(36) + '_' + Math.random().toString(36).substr(2, 9);
//       //   console.log("[SOCIAL REGISTRATION] 🔧 Generated test token:", finalCaptchaToken);
//       // }
//       // 
//       // console.log("[SOCIAL REGISTRATION] 📝 Final captcha_token:", finalCaptchaToken);
      
//       const lang = language || 'en';
//       const finalLang = lang === "be" ? "fr" : lang;
      
//       // Extract Google ID
//       const googleId = userData.google || userData.google_id || userData.sub;
      
//       console.log("[SOCIAL REGISTRATION] Extracted Google ID:", googleId);
      
//       if (!googleId) {
//         console.error("[SOCIAL REGISTRATION] No Google ID found");
//         return rejectWithValue([{ message: "Missing Google ID. Please try again." }]);
//       }
      
//       // Step 1: Register the user
//       const registrationData = {
//         email: userData.email,
//         first_name: userData.first_name,
//         last_name: userData.last_name,
//         type: userType,
//         status: status,
//         google: googleId,
//         lang: finalLang,
//         domain: userData.domain,
//         cgu: true,
//         newsletter: false,
//         // captcha_token: finalCaptchaToken
//       };
      
//       // Add phone if provided
//       if (userData.phone) {
//         registrationData.phone = userData.phone;
//       }
      
//       // Add social_reason if provided
//       if (userData.social_reason) {
//         registrationData.social_reason = userData.social_reason;
//       }
      
//       console.log("[SOCIAL REGISTRATION] Registration payload:", registrationData);
      
//       const registerResponse = await fetch(`${baseUrl}users?lang=${finalLang}`, {
//         method: "POST",
//         headers: {
//           "Content-Type": "application/json",
//           "Accept": "application/json"
//         },
//         body: JSON.stringify(registrationData),
//       });
      
//       const registerData = await registerResponse.json();
//       console.log("[SOCIAL REGISTRATION] Registration response:", registerData);
      
//       if (!registerResponse.ok) {
//         if (registerData.errors && Array.isArray(registerData.errors)) {
//           // // Check for CAPTCHA errors
//           // const hasCaptchaError = registerData.errors.some(err => 
//           //   err.code === 'captcha_token' || 
//           //   err.message?.toLowerCase().includes('captcha') ||
//           //   err.code === 'captcha'
//           // );
//           // if (hasCaptchaError) {
//           //   console.log("[SOCIAL REGISTRATION] ❌ CAPTCHA validation failed");
//           // }
//           // Clear flags on error
//           sessionStorage.removeItem('just_registered_for_booking');
//           sessionStorage.removeItem('just_logged_in_for_booking');
//           sessionStorage.removeItem('booking_space_id');
//           sessionStorage.removeItem('registration_timestamp');
//           sessionStorage.removeItem('has_pending_booking_after_oauth');
//           return rejectWithValue(registerData.errors);
//         }
//         throw new Error(registerData.message || "Registration failed");
//       }
      
//       console.log("[SOCIAL REGISTRATION] ✅ Registration successful!");
      
//       // ============================================================
//       // CRITICAL FIX: Login the user after successful registration
//       // ============================================================
//       try {
//         console.log("[SOCIAL REGISTRATION] 🔐 Logging in user...");
        
//         // Call the login endpoint to get a token
//         const loginResponse = await fetch(`${baseUrl}authenticate?lang=${finalLang}`, {
//           method: "POST",
//           headers: {
//             "Content-Type": "application/json",
//             "Accept": "application/json"
//           },
//           body: JSON.stringify({
//             email: userData.email,
//             google: googleId
//           }),
//         });
        
//         const loginData = await loginResponse.json();
//         console.log("[SOCIAL REGISTRATION] Login response:", loginData);
        
//         if (loginData.data?.token) {
//           const token = loginData.data.token;
//           const user = loginData.data.user;
          
//           localStorage.setItem('token', token);
//           localStorage.setItem('accessToken', token);
          
//           if (user?.type) {
//             localStorage.setItem('userType', user.type);
//           }
          
//           console.log("[SOCIAL REGISTRATION] ✅ User logged in successfully");
          
//           // ============================================================
//           // CRITICAL FIX: DO NOT clear booking flags on success
//           // Let the UI handle clearing them after booking is complete
//           // ============================================================
//           // Only remove these specific items:
//           sessionStorage.removeItem('registration_timestamp');
//           sessionStorage.removeItem('google_registration_data');
          
//           // ============================================================
//           // PRESERVE booking flags for the booking flow
//           // ============================================================
//           // DO NOT remove:
//           // - 'just_registered_for_booking' - needed for booking flow
//           // - 'booking_space_id' - needed for redirect
//           // - 'has_pending_booking_after_oauth' - needed for booking flow
//           // - 'pending_booking_data' - needed for CompactBookingForm
          
//           console.log("[SOCIAL REGISTRATION] ✅ Booking flags preserved for continuation");
//           console.log("[SOCIAL REGISTRATION] 📝 just_registered_for_booking:", sessionStorage.getItem('just_registered_for_booking'));
//           console.log("[SOCIAL REGISTRATION] 📝 booking_space_id:", sessionStorage.getItem('booking_space_id'));
//           console.log("[SOCIAL REGISTRATION] 📝 has_pending_booking_after_oauth:", sessionStorage.getItem('has_pending_booking_after_oauth'));
          
//           return {
//             data: { token, user },
//             message: "Registration successful"
//           };
//         } else {
//           console.error("[SOCIAL REGISTRATION] ❌ No token in login response");
//           throw new Error("Login failed after registration");
//         }
        
//       } catch (loginError) {
//         console.error("[SOCIAL REGISTRATION] ❌ Login error:", loginError);
//         // Clear flags on login error
//         sessionStorage.removeItem('just_registered_for_booking');
//         sessionStorage.removeItem('just_logged_in_for_booking');
//         sessionStorage.removeItem('booking_space_id');
//         sessionStorage.removeItem('registration_timestamp');
//         sessionStorage.removeItem('has_pending_booking_after_oauth');
//         return rejectWithValue([{ message: "Login failed after registration. Please try logging in manually." }]);
//       }
      
//     } catch (error) {
//       console.error("[SOCIAL REGISTRATION] ❌ Error:", error);
//       // Clear flags on error
//       sessionStorage.removeItem('just_registered_for_booking');
//       sessionStorage.removeItem('just_logged_in_for_booking');
//       sessionStorage.removeItem('booking_space_id');
//       sessionStorage.removeItem('registration_timestamp');
//       sessionStorage.removeItem('has_pending_booking_after_oauth');
//       return rejectWithValue([{ message: error.message || "Registration failed" }]);
//     }
//   }
// );

export const login = createAsyncThunk(
  "auth/login",
  async ({ credentials, language, targetUid, forBookingForm = false, spaceId = null }, { rejectWithValue, dispatch }) => {
    try {
      console.log("[LOGIN THUNK] Starting login...", { 
        email: credentials.email, 
        hasUid: !!targetUid 
      });
      
      // Set flag for booking form login - KEEP these until booking is complete
      if (forBookingForm && spaceId) {
        sessionStorage.setItem('just_logged_in_for_booking', 'true');
        sessionStorage.setItem('booking_space_id', spaceId);
        sessionStorage.setItem('login_timestamp', Date.now().toString());
        console.log("[LOGIN THUNK] Set booking login flag");
      }
      
      const requestBody = {
        email: credentials.email,
        password: credentials.password
      };
      
      if (targetUid) {
        requestBody.uid = targetUid;
        console.log("[LOGIN THUNK] Adding UID to request:", targetUid);
      }
      
      console.log("[LOGIN THUNK] Sending request to authenticate endpoint...");
      
      const response = await api.post(
        `authenticate?lang=${language}`,
        requestBody,
        {
          headers: {
            "Accept-Language": language,
            Accept: "application/json",
            "Content-Type": "application/json",
          },
        }
      );
      
      console.log("[LOGIN THUNK] Full response:", response.data);
      
      const responseData = response.data;
      
      if (!responseData.success) {
        console.error("[LOGIN THUNK] Backend returned success: false");
        throw new Error(responseData.message || "Login failed");
      }
      
      const token = responseData.data?.token;
      const userData = responseData.data?.user;
      const possibleToken = token || responseData.token || responseData.data?.accessToken;
      
      if (!possibleToken) {
        console.error("[LOGIN THUNK] ERROR: No token found in response!");
        throw new Error("No authentication token received");
      }

      console.log("[LOGIN THUNK] Token found:", possibleToken.substring(0, 20) + "...");
      
      localStorage.setItem('token', possibleToken);
      localStorage.setItem('accessToken', possibleToken);
      
      if (userData?.type) {
        console.log("[LOGIN THUNK] Storing user type:", userData.type);
        localStorage.setItem('userType', userData.type);
      }
      
      if (userData?.isImpersonated) {
        console.log("[LOGIN THUNK] This is an impersonation login");
        localStorage.setItem('isImpersonated', 'true');
        if (userData.originalAdmin) {
          localStorage.setItem('originalAdmin', JSON.stringify(userData.originalAdmin));
        }
      } else {
        localStorage.setItem('isImpersonated', 'false');
        localStorage.removeItem('originalAdmin');
      }
      
      // CRITICAL: DO NOT clear booking flags here - let CompactBookingForm handle it
      // The flags are needed to show the loading state after login
      
      return {
        data: {
          token: possibleToken,
          user: userData || {}
        },
        message: responseData.message
      };
      
    } catch (err) {
      console.error("[LOGIN THUNK] Login error:", err.response?.data || err);
      // Clear flags on error
      sessionStorage.removeItem('just_logged_in_for_booking');
      sessionStorage.removeItem('booking_space_id');
      sessionStorage.removeItem('login_timestamp');
      return rejectWithValue(handleError(err));
    }
  }
);


export const register = createAsyncThunk(
  "auth/register",
  async ({ user, forBookingForm = false, spaceId = null, captcha_token = null }, { dispatch, rejectWithValue }) => {
    console.log("[REGISTER THUNK] 🔍 Starting registration");
    console.log("[REGISTER THUNK] 📝 Received captcha_token:", captcha_token);
    console.log("[REGISTER THUNK] 📝 User object:", user);
    console.log("[REGISTER THUNK] 📝 User.captcha_token:", user?.captcha_token);
    
    // Check if using test key
    const isTestKey = process.env.REACT_APP_TURNSTILE_SITE_KEY === '1x00000000000000000000AA';
    console.log("[REGISTER THUNK] 🔧 Is test key:", isTestKey);
    
    // Get the token from either the parameter or the user object
    let finalCaptchaToken = captcha_token || user?.captcha_token;
    
    // If using test key and no token, generate one
    if (!finalCaptchaToken && isTestKey) {
      finalCaptchaToken = '1x' + Date.now().toString(36) + '_' + Math.random().toString(36).substr(2, 9);
      console.log("[REGISTER THUNK] 🔧 Generated test token:", finalCaptchaToken);
    }
    
    console.log("[REGISTER THUNK] 📝 Final captcha_token:", finalCaptchaToken);
    
    // Set flag for booking form registration
    if (forBookingForm && spaceId) {
      sessionStorage.setItem('just_registered_for_booking', 'true');
      sessionStorage.setItem('booking_space_id', spaceId);
      sessionStorage.setItem('registration_timestamp', Date.now().toString());
      console.log("[REGISTER THUNK] ✅ Set booking registration flag");
    }
    
    try {
      // Create the request body
      const requestBody = {
        ...user,
        captcha_token: finalCaptchaToken
      };
      
      console.log("[REGISTER THUNK] 📤 Final request body:", JSON.stringify(requestBody, null, 2));
      
      const response = await fetch(
        `${baseUrl}users?lang=${user.lang}`,
        {
          method: "POST",
          headers: {
            Accept: "application/json",
            "Content-Type": "application/json",
          },
          body: JSON.stringify(requestBody),
        }
      );

      if (!response.ok) {
        const errorData = await response.json();
        console.error("[REGISTER THUNK] ❌ Registration failed:", errorData);
        // Clear flags on error
        sessionStorage.removeItem('just_registered_for_booking');
        sessionStorage.removeItem('booking_space_id');
        sessionStorage.removeItem('registration_timestamp');
        return rejectWithValue(errorData.errors || [{ message: "Registration failed" }]);
      }

      const data = await response.json();
      console.log("[REGISTER THUNK] ✅ Registration successful:", data);

      const credentials = {
        email: user.email,
        password: user.password,
      };
      
      await dispatch(
        login({ credentials, language: user.lang, forBookingForm, spaceId })
      ).unwrap();

      return data;
    } catch (error) {
      console.error("[REGISTER THUNK] ❌ Registration error:", error);
      // Clear flags on error
      sessionStorage.removeItem('just_registered_for_booking');
      sessionStorage.removeItem('booking_space_id');
      sessionStorage.removeItem('registration_timestamp');
      return rejectWithValue(error.response?.data?.errors || [{ message: error.message || "Registration failed" }]);
    }
  }
);

// export const register = createAsyncThunk(
//   "auth/register",
//   async ({ user, forBookingForm = false, spaceId = null, captcha_token = null }, { dispatch, rejectWithValue }) => {
//     console.log("[REGISTER THUNK] 🔍 Starting registration");
//     // console.log("[REGISTER THUNK] 📝 Received captcha_token:", captcha_token);
//     console.log("[REGISTER THUNK] 📝 User object:", user);
//     // console.log("[REGISTER THUNK] 📝 User.captcha_token:", user?.captcha_token);
    
//     // // Check if using test key
//     // const isTestKey = process.env.REACT_APP_TURNSTILE_SITE_KEY === '1x00000000000000000000AA';
//     // console.log("[REGISTER THUNK] 🔧 Is test key:", isTestKey);
//     // 
//     // // Get the token from either the parameter or the user object
//     // let finalCaptchaToken = captcha_token || user?.captcha_token;
//     // 
//     // // If using test key and no token, generate one
//     // if (!finalCaptchaToken && isTestKey) {
//     //   finalCaptchaToken = '1x' + Date.now().toString(36) + '_' + Math.random().toString(36).substr(2, 9);
//     //   console.log("[REGISTER THUNK] 🔧 Generated test token:", finalCaptchaToken);
//     // }
//     // 
//     // console.log("[REGISTER THUNK] 📝 Final captcha_token:", finalCaptchaToken);
    
//     // Set flag for booking form registration
//     if (forBookingForm && spaceId) {
//       sessionStorage.setItem('just_registered_for_booking', 'true');
//       sessionStorage.setItem('booking_space_id', spaceId);
//       sessionStorage.setItem('registration_timestamp', Date.now().toString());
//       console.log("[REGISTER THUNK] ✅ Set booking registration flag");
//     }
    
//     try {
//       // Create the request body
//       const requestBody = {
//         ...user,
//         // captcha_token: finalCaptchaToken
//       };
      
//       console.log("[REGISTER THUNK] 📤 Final request body:", JSON.stringify(requestBody, null, 2));
      
//       const response = await fetch(
//         `${baseUrl}users?lang=${user.lang}`,
//         {
//           method: "POST",
//           headers: {
//             Accept: "application/json",
//             "Content-Type": "application/json",
//           },
//           body: JSON.stringify(requestBody),
//         }
//       );

//       if (!response.ok) {
//         const errorData = await response.json();
//         console.error("[REGISTER THUNK] ❌ Registration failed:", errorData);
//         // Clear flags on error
//         sessionStorage.removeItem('just_registered_for_booking');
//         sessionStorage.removeItem('booking_space_id');
//         sessionStorage.removeItem('registration_timestamp');
//         return rejectWithValue(errorData.errors || [{ message: "Registration failed" }]);
//       }

//       const data = await response.json();
//       console.log("[REGISTER THUNK] ✅ Registration successful:", data);

//       const credentials = {
//         email: user.email,
//         password: user.password,
//       };
      
//       await dispatch(
//         login({ credentials, language: user.lang, forBookingForm, spaceId })
//       ).unwrap();

//       return data;
//     } catch (error) {
//       console.error("[REGISTER THUNK] ❌ Registration error:", error);
//       // Clear flags on error
//       sessionStorage.removeItem('just_registered_for_booking');
//       sessionStorage.removeItem('booking_space_id');
//       sessionStorage.removeItem('registration_timestamp');
//       return rejectWithValue(error.response?.data?.errors || [{ message: error.message || "Registration failed" }]);
//     }
//   }
// );

// Admin Login with UID

export const adminLoginWithUid = createAsyncThunk(
  "auth/adminLoginWithUid",
  async ({ email, password, targetUid, language, forBookingForm = false, spaceId = null }, { rejectWithValue, dispatch }) => {
    try {
      console.log("[ADMIN LOGIN] Starting admin login with UID:", { email, targetUid });
      
      return await dispatch(login({ 
        credentials: { email, password },
        language,
        targetUid,
        forBookingForm,
        spaceId
      })).unwrap();
      
    } catch (err) {
      console.error("[ADMIN LOGIN] Admin login error:", err.response?.data || err);
      return rejectWithValue(handleError(err, "Admin login failed"));
    }
  }
);

// Admin Login to User (Impersonation)
export const adminLoginToUser = createAsyncThunk(
  "auth/adminLoginToUser",
  async ({ targetUid, language, forBookingForm = false, spaceId = null }, { getState, rejectWithValue }) => {
    try {
      const state = getState();
      const currentToken = state.auth.token;
      
      if (!currentToken) {
        return rejectWithValue([{ message: "No active session found" }]);
      }

      const response = await api.post(
        `admin/impersonate/${targetUid}?lang=${language}`,
        {},
        {
          headers: {
            Authorization: `Bearer ${currentToken}`,
            "Accept-Language": language,
            Accept: "application/json",
          },
        }
      );

      const token = response?.data?.token;
      const userData = response?.data?.user;

      if (userData.isImpersonated) {
        const originalUser = {
          id: state.auth.currentUser?.id || state.auth.currentUser?._id,
          type: state.auth.currentUser?.type,
          email: state.auth.currentUser?.email,
          name: state.auth.currentUser?.name
        };
        localStorage.setItem('originalAdmin', JSON.stringify(originalUser));
        localStorage.setItem('isImpersonated', 'true');
      }

      setToken(token);
      setUserType(userData.type);
      
      // Set flag for booking form impersonation login
      if (forBookingForm && spaceId) {
        sessionStorage.setItem('just_logged_in_for_booking', 'true');
        sessionStorage.setItem('booking_space_id', spaceId);
        sessionStorage.setItem('login_timestamp', Date.now().toString());
      }
      
      return response.data;
    } catch (err) {
      console.error("Admin login error:", err);
      return rejectWithValue(handleError(err, "Admin login failed"));
    }
  }
);

// Exit Impersonation
export const exitImpersonation = createAsyncThunk(
  "auth/exitImpersonation",
  async ({ language }, { getState, rejectWithValue, dispatch }) => {
    try {
      const originalAdmin = JSON.parse(localStorage.getItem('originalAdmin'));
      
      if (!originalAdmin) {
        await dispatch(logout({ language }));
        return;
      }

      const response = await api.post(
        `admin/restore-session?lang=${language}`,
        { adminId: originalAdmin.id },
        {
          headers: {
            "Accept-Language": language,
            Accept: "application/json",
          },
        }
      );

      const token = response?.data?.token;
      const userData = response?.data?.user;

      setToken(token);
      setUserType(userData.type);
      
      localStorage.removeItem('originalAdmin');
      localStorage.removeItem('isImpersonated');
      
      // Clear any booking flags
      sessionStorage.removeItem('just_logged_in_for_booking');
      sessionStorage.removeItem('booking_space_id');
      sessionStorage.removeItem('login_timestamp');
      sessionStorage.removeItem('just_registered_for_booking');
      sessionStorage.removeItem('registration_timestamp');
      
      return response.data;
    } catch (err) {
      console.error("Exit impersonation error:", err);
      await dispatch(logout({ language }));
      return rejectWithValue(handleError(err, "Failed to exit impersonation"));
    }
  }
);

// Get Current User
export const getCurrentUser = createAsyncThunk(
  'auth/getCurrentUser',
  async ({ language }, thunkAPI) => {
    try {
      const token = getToken();
      if (!token) {
        throw new Error('No token found');
      }

      const response = await axios.get(`${baseUrl}users`, {
        headers: {
          Authorization: `Bearer ${token}`,
          'Accept-Language': language,
          Accept: 'application/json',
        },
      });

      return response.data.data;
    } catch (error) {
      return thunkAPI.rejectWithValue(handleAuthError(error, thunkAPI));
    }
  }
);

// Logout
export const logout = createAsyncThunk(
  "auth/logout",
  async ({ language }, { dispatch, rejectWithValue }) => {
    try {
      const token = getToken();
      if (token) {
        await api.post(
          `authenticate/invalidate?lang=${language}`,
          {},
          {
            headers: {
              "Accept-Language": language,
              Authorization: `Bearer ${token}`,
            },
          }
        );
      }
    } catch (err) {
      return rejectWithValue(handleError(err, "Logout failed"));
    } finally {
      clearToken();
      clearUserType();
      localStorage.removeItem('originalAdmin');
      localStorage.removeItem('isImpersonated');
      // Clear all booking-related sessionStorage
      clearBookingSessionData();

      dispatch(resetAuthState());
      dispatch(setFavorites([]));
      localStorage.removeItem("favorites");
    }
  }
);